/**
 * ReportFiltersBar — R5 (2026-05-17), extended R7a (2026-05-18).
 *
 * Shared filter UI for the reports suite. Reuses R4 patterns:
 * EventsOrgFilter typeahead (with sanitized .or() search), dropdowns
 * for state/type/status, date pickers. Each report renders the
 * subset of filters it actually uses by passing the appropriate
 * slots.
 *
 * R7a additions: rep dropdown + "Include Unattributed" toggle. Rep
 * filter pulls all active sales_positions on mount (bounded at 500).
 * The toggle is also used by SalesByRep (R5) which previously kept it
 * as local state — now promoted to the shared filter state.
 *
 * Filter state is fully controlled by the parent so each report owns
 * its own query key for tanstack-query cache discrimination.
 */

import React from "react";
import { useQuery } from "@tanstack/react-query";
// 2026-06-07-post-deploy: code/name pairs so the filter sends
// 2-letter abbrevs (matches DB), not full names.
import { US_STATE_OPTIONS } from "../../types/list.types";
import { EVENT_TYPES } from "../../utils/constants";
import EventsOrgFilter from "../events/EventsOrgFilter";
import { listSalesPositions } from "../../services/salesActivities.service";

export interface ReportFilterState {
  state?: string;
  type?: string;
  status?: string;
  orgId?: string;
  orgLabel?: string;
  dateFrom?: string;
  dateTo?: string;
  repPositionId?: string;
  includeUnattributed?: boolean;
}

interface Props {
  filters: ReportFilterState;
  onChange: (next: ReportFilterState) => void;
  showState?: boolean;
  showType?: boolean;
  showStatus?: boolean;
  showOrg?: boolean;
  showDateRange?: boolean;
  showRep?: boolean;
  showIncludeUnattributed?: boolean;
}

const STATUS_OPTIONS = [
  { value: "draft", label: "Draft" },
  { value: "scheduled", label: "Scheduled" },
  { value: "in_progress", label: "In Progress" },
  { value: "completed", label: "Completed" },
  { value: "cancelled", label: "Cancelled" },
];

const ReportFiltersBar: React.FC<Props> = ({
  filters,
  onChange,
  showState = true,
  showType = true,
  showStatus = false,
  showOrg = true,
  showDateRange = true,
  showRep = false,
  showIncludeUnattributed = false,
}) => {
  const update = (patch: Partial<ReportFilterState>) =>
    onChange({ ...filters, ...patch });

  const { data: positions } = useQuery({
    queryKey: ["report-rep-filter-positions"],
    queryFn: listSalesPositions,
    enabled: showRep,
    staleTime: 5 * 60 * 1000,
  });

  return (
    <div className="flex flex-wrap items-end gap-3 rounded-lg border border-gray-200 bg-white p-3">
      {showDateRange && (
        <>
          <div>
            <label className="block text-xs font-medium text-gray-600">
              From
            </label>
            <input
              type="date"
              value={filters.dateFrom || ""}
              onChange={(e) =>
                update({ dateFrom: e.target.value || undefined })
              }
              className="rounded border border-gray-300 px-2 py-1 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600">
              To
            </label>
            <input
              type="date"
              value={filters.dateTo || ""}
              onChange={(e) => update({ dateTo: e.target.value || undefined })}
              className="rounded border border-gray-300 px-2 py-1 text-sm"
            />
          </div>
        </>
      )}
      {showState && (
        <div>
          <label className="block text-xs font-medium text-gray-600">
            State
          </label>
          <select
            value={filters.state || ""}
            onChange={(e) => update({ state: e.target.value || undefined })}
            className="rounded border border-gray-300 px-2 py-1 text-sm"
          >
            <option value="">All states</option>
            {US_STATE_OPTIONS.map((s) => (
              <option key={s.code} value={s.code}>
                {s.name}
              </option>
            ))}
          </select>
        </div>
      )}
      {showType && (
        <div>
          <label className="block text-xs font-medium text-gray-600">
            Type
          </label>
          <select
            value={filters.type || ""}
            onChange={(e) => update({ type: e.target.value || undefined })}
            className="rounded border border-gray-300 px-2 py-1 text-sm"
          >
            <option value="">All types</option>
            {EVENT_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </div>
      )}
      {showStatus && (
        <div>
          <label className="block text-xs font-medium text-gray-600">
            Status
          </label>
          <select
            value={filters.status || ""}
            onChange={(e) => update({ status: e.target.value || undefined })}
            className="rounded border border-gray-300 px-2 py-1 text-sm"
          >
            <option value="">All statuses</option>
            {STATUS_OPTIONS.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </div>
      )}
      {showOrg && (
        <EventsOrgFilter
          value={filters.orgId || ""}
          label={filters.orgLabel || ""}
          onChange={(orgId: string, orgLabel: string) =>
            update({ orgId, orgLabel })
          }
        />
      )}
      {showRep && (
        <div>
          <label className="block text-xs font-medium text-gray-600">
            Credited Rep
          </label>
          <select
            value={filters.repPositionId || ""}
            onChange={(e) =>
              update({ repPositionId: e.target.value || undefined })
            }
            className="rounded border border-gray-300 px-2 py-1 text-sm"
          >
            <option value="">All reps</option>
            {(positions ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {(p.assigned_name || p.title) +
                  (p.is_active ? "" : " (inactive)")}
              </option>
            ))}
          </select>
        </div>
      )}
      {showIncludeUnattributed && (
        <label className="inline-flex items-center gap-2 self-end pb-1 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={filters.includeUnattributed ?? true}
            onChange={(e) => update({ includeUnattributed: e.target.checked })}
            className="rounded border-gray-300"
          />
          Include Unattributed
        </label>
      )}
    </div>
  );
};

export default ReportFiltersBar;
